import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { customerService } from '../../services/customerService';

const initialState = {
  customers: [],
  currentCustomer: null,
  customerVehicles: [],
  stats: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await customerService.getCustomers(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch customers');
    }
  }
);

export const fetchCustomer = createAsyncThunk(
  'customers/fetchCustomer',
  async (id, { rejectWithValue }) => {
    try {
      const data = await customerService.getCustomer(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch customer');
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData, { rejectWithValue }) => {
    try {
      const data = await customerService.createCustomer(customerData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create customer');
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, customerData }, { rejectWithValue }) => {
    try {
      const data = await customerService.updateCustomer(id, customerData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update customer');
    }
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id, { rejectWithValue }) => {
    try {
      await customerService.deleteCustomer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete customer');
    }
  }
);

export const fetchCustomerVehicles = createAsyncThunk(
  'customers/fetchVehicles',
  async (customerId, { rejectWithValue }) => {
    try {
      const data = await customerService.getCustomerVehicles(customerId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch customer vehicles');
    }
  }
);

export const fetchCustomerStats = createAsyncThunk(
  'customers/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await customerService.getCustomerStats();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch customer stats');
    }
  }
);

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCustomer: (state) => {
      state.currentCustomer = null;
      state.customerVehicles = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || action.payload.length,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single customer
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.currentCustomer = action.payload;
      })
      // Create customer
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.customers.unshift(action.payload);
      })
      // Update customer
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        if (state.currentCustomer?.id === action.payload.id) {
          state.currentCustomer = action.payload;
        }
      })
      // Delete customer
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.customers = state.customers.filter(customer => customer.id !== action.payload);
      })
      // Fetch customer vehicles
      .addCase(fetchCustomerVehicles.fulfilled, (state, action) => {
        state.customerVehicles = action.payload;
      })
      // Fetch stats
      .addCase(fetchCustomerStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearError, clearCurrentCustomer } = customerSlice.actions;
export default customerSlice.reducer;